/*
 * Copyright, AlpineReplay, Inc. 2021. All rights reserved.
 * Author: Brian Bal, Cameron Hassan
 */
import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { valueFor, getShouldShowTraceTeam } from "../../util/helpers"
import { PanelTitle } from "../../components/PanelTitle"
import { UserControls } from "../../components/UserControls"
import * as links from "../../util/links"
import { SharePlaylistCard } from "../../components/SharePlaylistCard"
import * as stat from "../../util/analytics"
import {
    Color,
    Label,
    Element,
    useToken,
    Link,
    isSmallMobileScreen,
    TraceTeamInfoCard,
} from "trux"
import { useGetMomentCollectionsByUserAndGameQuery } from "../../generated/graphql"
import EventItem from "./EventItem"
import "./EventsPanel.css"

function EventsPanel(props) {
    const [multicamOpen, setMulticamOpen] = useState(false)

    const token = useToken("EventsPanel")
    const tokenReady = token != null

    const {
        data: momentsData,
        loading,
        error,
    } = useGetMomentCollectionsByUserAndGameQuery({
        variables: {
            token: token,
            user_id: props.user?.user_id,
            game_id: props.gameId,
        },
        skip: !tokenReady || !props.user,
    })
    let momentsInTraceId = momentsData
        ? momentsData.momentCollectionsByUserAndGame
        : []

    const showTraceTeamInfoCard = getShouldShowTraceTeam(
        props.game,
        props.user,
        props.divisions
    )

    useEffect(() => {
        // componentDidMount
        document.addEventListener("trc-video-info", onVideoUpdate)

        stat.statGameEvent(props.teamId, props.gameId, "panel:all-notes")

        // componentWillUnmount
        return () => {
            document.removeEventListener("trc-video-info", onVideoUpdate)
        }
    }, [])

    useEffect(() => {
        setTimeout(() => {
            scrollElementIntoViewIfNeeded()
        }, 100)
    })

    function closePressed(e) {
        let camera = props.currentSource?.meta?.camera?.name
            ? props.currentSource.meta.camera.name
            : null
        let eventNumberWithoutCamera = props.eventNumber
            ? props.eventNumber.split(":")[0]
            : null
        let eventProp =
            camera && camera !== "all" && camera !== "tracecam"
                ? `${eventNumberWithoutCamera}:${camera}`
                : eventNumberWithoutCamera
        let noPanelUrl = links.gameUrl(
            props.teamName,
            props.playlistId,
            props.gameId,
            eventProp
        )
        props.history.push(noPanelUrl)
    }

    function scrollElementIntoViewIfNeeded() {
        const selectedRow = document.querySelector(
            ".EventsPanel .MomentRow.expanded"
        )

        if (document.body.className.includes("locked")) {
            return
        }

        if (
            selectedRow &&
            (props.location.pathname.includes("moments") ||
                isSmallMobileScreen())
        ) {
            const filterElem = document.querySelector(
                ".PanelTitle .playlist-filters"
            )

            const selectedRowBounds = selectedRow?.getBoundingClientRect()
            const filterElemBounds = filterElem?.getBoundingClientRect()

            const scrollElement = isSmallMobileScreen()
                ? document.querySelector(
                      ".App .EventsPanel .side-panel-content"
                  )
                : window

            const bottomBanner = document.querySelector(".Row.BottomBanner")

            // is row in view
            const st = Math.floor(selectedRowBounds.top)
            const sb = Math.floor(selectedRowBounds.bottom)
            const min = Math.floor(filterElemBounds.bottom)
            const max = Math.floor(
                window.innerHeight -
                    (bottomBanner ? bottomBanner.offsetHeight : 0)
            )

            if (st >= min && sb <= max) {
                return
            }

            if (st < min) {
                // scroll up to get top of element in frame
                scrollElement.scrollBy(0, (min - st) * -1)
            } else if (sb > max) {
                // scroll down to get bottom of element in frame
                scrollElement.scrollBy(0, sb - max)
            }
        }
    }

    const handleShowTraceTeamDialog = () => {
        const history = props.history
        history.push(history.location.pathname + "/upgrade-trace-team")
    }

    const handleOnShareClick = () => {
        const history = props.history
        let forwardingURL = history.location.pathname.replace("/moments", "")
        forwardingURL += "/share"
        history.push(forwardingURL)
    }

    function onVideoUpdate(e) {
        // Update multicam open state
        let vi = e.detail
        if (vi) {
            setMulticamOpen((currState) =>
                currState !== vi.multicamOpen ? vi.multicamOpen : currState
            )
        }

        // This updates moment row styles
        let duration = e.detail.duration
        let time = e.detail.currentTime
        let per = Math.round((time / duration) * 1000) / 10

        if (per < 2) {
            per = 2
        }
        if (per > 99) {
            per = 99
        }

        let rows = document.querySelectorAll(".MomentRow")
        let el = document.querySelector(".game-events-list .MomentRow.expanded")
        for (let row of rows) {
            if (el !== row) {
                row.style = ""
            }
        }
        if (el) {
            el.style = `background: linear-gradient(
                90deg, var(--grey5-color) 0%,
                var(--grey5-color) ${per}%,
                var(--white2-color) ${per}%,
                var(--white2-color) 99%
            );`
        }
    }

    function filterCameras(cameraName) {
        props.selectCamera(cameraName)
    }

    function filterHalves(selectedHalf) {
        props.selectHalf(selectedHalf)
    }

    function filterInnings(selectedInning) {}

    // Render
    let chooseUrl = "/"
    if (props.currentPlaylist) {
        chooseUrl = window.location.hash.replace("/moments", "") + "/choose"
    }
    var panelClass = ""
    var containerClass = "side-panel events-panel"
    if (
        props.location.pathname.includes("moments") ||
        props.location.pathname.includes("choose")
    ) {
        containerClass += " events-panel-show"
        panelClass = "show"
    }
    if (!props.match.isExact && props.location.pathname.includes("/players")) {
        containerClass += " no-notes"
    }
    if (props.spotlightCount === 0) {
        containerClass += " no-spotlight"
    }
    if (multicamOpen) {
        panelClass += " offset-for-source-picker-open"
    }

    return (
        <Element className={"EventsPanel " + panelClass}>
            <Element className="side-panel-backdrop" onClick={closePressed} />
            <Element className={containerClass}>
                <Element id="video-size" />
                <PanelTitle
                    onClosePressed={closePressed}
                    allCameras={props.allCameras}
                    clipViewsEnabled={props.clipViewsEnabled}
                    currentPlaylist={props.currentPlaylist}
                    currentEvent={props.currentEvent}
                    events={props.events}
                    filterCameras={filterCameras}
                    filterHalves={filterHalves}
                    filterInnings={filterInnings}
                    userControls={
                        <Element className="extras-content">
                            <UserControls
                                trkContext="Video Controls"
                                className="user-controls-display"
                                user={props.user}
                                event={props.currentEvent}
                                game={props.game}
                                history={props.history}
                                teamName={props.teamName}
                                playlistId={props.playlistId}
                                gameId={props.gameId}
                                eventNumber={props.eventNumber}
                                notesUrl={props.notesUrl}
                                eventCount={props.eventCount}
                                userProfileUrl={props.userProfileUrl}
                                chooseUrl={chooseUrl}
                                currentPlaylist={props.currentPlaylist}
                                acl={props.acl}
                            />
                        </Element>
                    }
                    extras={
                        <Element className="extras-content">
                            {isSmallMobileScreen() && (
                                <Element className="side-panel-content">
                                    <ul className="game-events-list">
                                        {props.events.map((event) => (
                                            // eslint-disable-next-line react/jsx-key
                                            <EventItem
                                                event={event}
                                                momentsInTraceId={
                                                    momentsInTraceId
                                                }
                                                teamName={props.teamName}
                                                gameId={props.gameId}
                                                playlistId={props.playlistId}
                                                eventNumber={props.eventNumber}
                                                history={props.history}
                                                players={props.players}
                                                spotlightEnabled={
                                                    props.spotlightEnabled
                                                }
                                                currentPlaylist={
                                                    props.currentPlaylist
                                                }
                                                currentCamera={
                                                    props.currentCamera
                                                }
                                                homeTeamColor={
                                                    props.homeTeamColor
                                                }
                                                awayTeamColor={
                                                    props.awayTeamColor
                                                }
                                                currentSource={
                                                    props.currentSource
                                                }
                                                selectedCamera={
                                                    props.selectedCamera
                                                }
                                            />
                                        ))}
                                        <Label
                                            small
                                            center
                                            padding="2 3"
                                            color={Color.Grey3}
                                            className="trace-bot-message"
                                        >
                                            TRACE_BOT automatically created{" "}
                                            {props.numTotalEvents} moments for
                                            this game.
                                        </Label>
                                    </ul>
                                    <Element className="more-playlists">
                                        <Label type={Label.Type.Title3}>
                                            Looking for more?
                                        </Label>
                                        <Link
                                            data-trk-act={`Playlist Selector Pressed`}
                                            className="playlist-selector"
                                            to={
                                                window.location.hash.replace(
                                                    "/moments",
                                                    ""
                                                ) + "/choose"
                                            }
                                        >
                                            Select another playlist
                                        </Link>
                                    </Element>
                                </Element>
                            )}
                        </Element>
                    }
                />
                {!isSmallMobileScreen() && (
                    <Element className="side-panel-content">
                        <ul className="game-events-list">
                            {props.events.map((event) => (
                                // eslint-disable-next-line react/jsx-key
                                <EventItem
                                    event={event}
                                    momentsInTraceId={momentsInTraceId}
                                    teamName={props.teamName}
                                    gameId={props.gameId}
                                    playlistId={props.playlistId}
                                    eventNumber={props.eventNumber}
                                    history={props.history}
                                    players={props.players}
                                    spotlightEnabled={props.spotlightEnabled}
                                    currentPlaylist={props.currentPlaylist}
                                    currentCamera={props.currentCamera}
                                    homeTeamColor={props.homeTeamColor}
                                    awayTeamColor={props.awayTeamColor}
                                    currentSource={props.currentSource}
                                    selectedCamera={props.selectedCamera}
                                />
                            ))}
                            {showTraceTeamInfoCard && (
                                <TraceTeamInfoCard
                                    renderAsRow={true}
                                    onButtonClick={(e) =>
                                        handleShowTraceTeamDialog(e)
                                    }
                                />
                            )}
                            <Label
                                small
                                center
                                padding="2 3"
                                color={Color.Grey3}
                                className="trace-bot-message"
                            >
                                TRACE_BOT automatically created{" "}
                                {props.numTotalEvents} moments for this game.
                            </Label>
                            <SharePlaylistCard
                                paddingTop="8"
                                onShareClick={(e) => handleOnShareClick(e)}
                            />
                        </ul>
                        <Element className="more-playlists">
                            <Label type={Label.Type.Title3}>
                                Looking for more?
                            </Label>
                            <Link
                                data-trk-act={`Playlist Selector Pressed`}
                                className="playlist-selector"
                                to={
                                    window.location.hash.replace(
                                        "/moments",
                                        ""
                                    ) + "/choose"
                                }
                            >
                                Select another playlist
                            </Link>
                        </Element>
                    </Element>
                )}
            </Element>
        </Element>
    )
}

const mapStateToProps = (state, props) => {
    let teamName = props.match.params.team_name
    let gameId = parseInt(props.match.params.game_id, 10)
    let playlistId = props.match.params.playlist_id
    let eventNumber = props.match.params.event_number
    let currentPlaylist = state.video.value.playlist
    let events = state.video.value.playlistEvents
    let currentEvent = state.video.value.event
    let currentSource = state.video.value.source
    let currentCamera = state.video.value.camera
    let allEvents = state.game.value ? state.game.value.events : []
    let players = state.game.value ? state.game.value.home_team.players : []
    let user = state.user.value
    let game = state.game.value
    let divisions = state.divisions.value
    let acl = state.acl.value
    let allCameras = state.allCameras
    let selectedCamera = state.selectedCamera

    let spotlightEnabled = state.settings.spotlightEnabled
    let spotlightCount = 0

    let clipViewsEnabled = gameId > 12200

    if (currentPlaylist && currentPlaylist.scope) {
        for (let ev of events) {
            if (ev.has_spotlight) {
                spotlightCount += 1
            }
        }
    }

    let homeTeamColor = valueFor(
        "game.value.home_team.jersey_color",
        "#000000",
        state
    )
    let awayTeamColor = valueFor(
        "game.value.away_team.jersey_color",
        "#ffffff",
        state
    )

    return {
        spotlightEnabled,
        spotlightCount,
        teamName,
        gameId,
        divisions,
        currentPlaylist,
        playlistId,
        eventNumber,
        currentEvent,
        currentSource,
        currentCamera,
        events,
        clipViewsEnabled,
        players,
        user,
        game,
        numTotalEvents: allEvents.length,
        homeTeamColor,
        awayTeamColor,
        acl,
        allCameras,
        selectedCamera,
    }
}

const mapDispatchToProps = (dispatch, getState) => {
    return {
        selectBaseballSide: (side) => {
            dispatch({
                type: "SET_FILTER",
                key: 'selectBaseballSide',
                value: side,
            })
        },
        selectBaseballTag: (tag) => {
            dispatch({
                type: "SET_FILTER",
                key: 'selectBaseballTag',
                value: tag,
            })
        },
        selectCamera: (selectedCamera) => {
            dispatch({
                type: "SET_FILTER",
                key: "selectedCamera",
                value: selectedCamera,
            })
        },
        selectHalf: (selectedHalf) => {
            dispatch({
                type: "SET_FILTER",
                key: "selectedHalf",
                value: selectedHalf,
            })
        },
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(EventsPanel)
